import { AxiosInstance } from 'axios';
import _ from 'lodash';

import {
    CommonRestResponse,
    CustomAxiosRequestConfig,
} from 'scbk-services/types/http/axios-extension';
import { RequestType } from '../constants/request';

import { JwtEndpoint, JwtErrorCodes, NormalizedJwtResponse } from './jwt-commons';
import { JwtProcessStateManager } from './jwt-process-state';
import { requestRefreshToken, resolveLoginResponse, resolveRefreshResponse } from './jwt-resolvers';
import { JwtTokenRepository } from './jwt-token-repository';

let processManager: JwtProcessStateManager = JwtProcessStateManager.getInstance();
let tokenRepository: JwtTokenRepository = JwtTokenRepository.getInstance();

/**
 * JWT관련 AXIOS 인터셉터 설정 함수
 * @param instance
 */
export const addJwtInterceptors = (
    instance: AxiosInstance,
    { jwtUseMode = 'NONE' }: { jwtUseMode?: string },
) => {
    console.log('jwt-interceptors > addJwtInterceptors - jwtUseMode : ', jwtUseMode);

    if (!_.isEqual(jwtUseMode, 'INTERNAL') && !_.isEqual(jwtUseMode, 'KONG')) {
        console.log('jwt-interceptors > addJwtInterceptors - skip(jwt인터셉터 미등록)');
        return;
    }

    console.log('jwt-interceptors > addJwtInterceptors - register(jwt인터셉터 등록시작) : ');

    /**
     * API 호출전 Access Token 적재
     */
    instance.interceptors.request.use(
        (config: CustomAxiosRequestConfig) => {
            const requestType: RequestType | undefined = config.requestType;

            console.log('jwt-interceptors > request(요청처리) - requestConfig : ', {
                requestType,
                baseUrl: config?.baseURL,
                url: config?.url,
            });

            if (!_.isEqual(requestType, RequestType.REST)) {
                return config;
            }

            console.log('jwt-interceptors > request(요청처리) - processState', {
                nowHandling401: processManager.isNowHandling401(),
                nowHandling403: processManager.isNowHandling403(),
                failQueue: processManager.getFailedQueue(),
            });

            const accessToken = tokenRepository.getAccessToken();
            console.log('jwt-interceptors > request(요청처리) - Access Token : ', accessToken);

            if (accessToken && config.headers) {
                config.headers.Authorization = `Bearer ${accessToken}`;
            }

            return config;
        },
        // 요청 강제 취소, 다른 인터셉터 오류, 전송 전 오류 등
        (error) => {
            console.warn('jwt-interceptors > request(요청처리) - error : ', error);
            console.log('jwt-interceptors > request(요청처리) - errorConfig : ', {
                baseUrl: error.config?.baseURL,
                url: error.config?.url,
            });

            //요청 호출자에게 에러 그대로 반환
            return Promise.reject(error);
        },
    );

    /**
     * 발급 토큰 저장 & 재발급 처리
     */
    instance.interceptors.response.use(
        <Data>(response: CommonRestResponse<Data>) => {
            const config = response?.config as CustomAxiosRequestConfig;
            const requestType: RequestType | undefined = config?.requestType;

            console.log('jwt-interceptors > request(요청처리) - requestConfig : ', {
                requestType,
                baseUrl: config?.baseURL,
                url: config?.url,
            });
            console.log('jwt-interceptors > response(응답처리) - response : ', response);

            //로그인 응답 처리
            if (
                _.isEqual(requestType, RequestType.REST) &&
                config.url?.includes(JwtEndpoint.Login)
            ) {
                const normalizedLoginResponse: NormalizedJwtResponse =
                    resolveLoginResponse(response);

                console.log(
                    'jwt-interceptors > response(응답처리) - normalizedLoginResponse : ',
                    normalizedLoginResponse,
                );

                if (normalizedLoginResponse.isSuccess) {
                    //정상 응답
                    const { accessToken, refreshToken } = normalizedLoginResponse;

                    console.log('jwt-interceptors > response(응답처리) - tokenInfo : ', {
                        accessToken,
                        refreshToken,
                    });

                    tokenRepository.saveTokens({
                        accessToken: accessToken,
                        refreshToken: refreshToken,
                    });
                } else {
                    //비정상 응답
                    return processManager.handleJwtError(JwtErrorCodes.ISSUE_NEW_FAILURE);
                }
            }

            return response;
        },
        async (error) => {
            const originalRequest = error.config;
            const requestType: RequestType | undefined = error.config?.requestType;
            const httpStatus = error.response?.status;

            console.warn('jwt-interceptors > response(응답처리) - error : ', error);
            console.log('jwt-interceptors > response(응답처리) - errorConfig : ', {
                requestType,
                baseUrl: error.config?.baseURL,
                url: error.config?.url,
            });

            if (!_.isEqual(requestType, RequestType.REST)) {
                console.log('jwt-interceptors > response(응답처리) - not restful');

                // Experience API 요청이 아니면 에러 그래로 반환
                return Promise.reject(error);
            }

            console.log('jwt-interceptors > response(응답처리) - httpStatus/retry', {
                httpStatus,
                originalRequestRetry: originalRequest?._retry,
            });

            if (httpStatus === 401 && !originalRequest?._retry) {
                console.log(
                    'jwt-interceptors > response(응답처리) - 401 Unauthorized : need newToken',
                );

                console.log('jwt-interceptors > response(응답처리) - processStateInfo', {
                    nowHandling401: processManager.isNowHandling401(),
                    nowHandling403: processManager.isNowHandling403(),
                    failQueue: processManager.getFailedQueue(),
                });

                // 이전 요청이 토큰 재발급 중이라면 대기큐에 저장 후 Skip
                if (processManager.isNowHandling401()) {
                    return processManager.holdFailedRequest(instance, error);
                }

                originalRequest._retry = true;
                processManager.setNowHandling401(true);

                // 토큰 재발급
                const refreshResponse = await requestRefreshToken(error);
                console.log(
                    'jwt-interceptors > response(응답처리) - refreshResponse : ',
                    refreshResponse,
                );

                // 토큰 표준 응답 생성
                const normalizedRefreshResponse: NormalizedJwtResponse | null =
                    resolveRefreshResponse(refreshResponse);

                console.log(
                    'jwt-interceptors > response(응답처리) - normalizedRefreshResponse : ',
                    normalizedRefreshResponse,
                );

                // 비정상 응답 처리
                if (!normalizedRefreshResponse || !normalizedRefreshResponse.isSuccess) {
                    return processManager.handleJwtError(JwtErrorCodes.REFRESH_FAILURE);
                }

                const { accessToken: newAccessToken, refreshToken: newRefreshToken } =
                    normalizedRefreshResponse;

                console.log('jwt-interceptors > response(응답처리) - newTokenInfo : ', {
                    accessToken: newAccessToken,
                    refreshToken: newRefreshToken,
                });

                // 신규 발급 토큰 저장
                tokenRepository.saveTokens({
                    refreshToken: newRefreshToken,
                    accessToken: newAccessToken,
                });

                // 대기큐 처리(성공 처리)
                processManager.processQueue(null, newAccessToken);

                // JWT 처리 상태 초기화
                processManager.resetJwtProcessState(false);

                // 원거래 처리 AT 토큰 셋팅
                originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;

                // 원거래 처리
                return instance(originalRequest);

                // 403 Forbidden 처리
            } else if (httpStatus === 403) {
                if (!processManager.isNowHandling403()) {
                    processManager.setNowHandling403(true);
                    console.log(
                        'jwt-interceptors > response(응답처리) - 403 Forbidden : fail to verification Token',
                    );
                    return processManager.handleJwtError(JwtErrorCodes.FORBIDDEN);
                }

                return new Promise(() => {});
            }

            // 기타 오류 응답 - 그대로 반환
            return Promise.reject(error);
        },
    );

    console.log('jwt-interceptors > addJwtInterceptors - complete(jwt인터셉터 등록 완료)');
};
